import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import LeadsService from '@/services/LeadsService'

import PersonalInfoSection from '../common/PersonalInfoSection'
import LeadDetailsSection from '../common/LeadDetailsSection'
import CommunicationHistory from './CommunicationHistory'

export default function LeadEdit() {
	const { id } = useParams()
	const navigate = useNavigate()

	const [fetching, setFetching] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const [formData, setFormData] = useState({
		customerName: '',
		customerEmail: '',
		customerTel: '',
		source: '',
		campaign: '',
		status: '',
		note: '',
		communications: [],
	})

	// Generic setter for shared sections
	const setField = useCallback((name, value) => {
		setFormData(prev => ({ ...prev, [name]: value }))
	}, [])

	// Submit (no unexpected fields; API doesn't expect communications)
	const handleSubmit = useCallback(
		async e => {
			e.preventDefault()
			if (submitting) return

			setError(null)
			setSubmitting(true)
			try {
				const { communications, ...payload } = formData
				const { success, error } = await LeadsService.updateLead(id, payload)
				if (success) {
					navigate('/leads', {
						state: { message: `Lead ${formData.customerName} has been updated successfully.` },
					})
				} else {
					setError(error || 'Failed to update lead')
				}
			} catch (err) {
				console.error('Unexpected error during lead update:', err)
				setError('An unexpected error occurred while updating the lead.')
			} finally {
				setSubmitting(false)
			}
		},
		[formData, id, navigate, submitting]
	)

	// Fetch + hydrate form
	useEffect(() => {
		let alive = true
		;(async () => {
			setFetching(true)
			setError(null)
			try {
				const { data, error } = await LeadsService.getLeadById(id)
				if (!alive) return
				if (error) throw new Error(error)

				const comms = Array.isArray(data?.communications) ? data.communications : []
				// Latest note by date (desc)
				const latestNote =
					comms.length > 0 ? [...comms].sort((a, b) => new Date(b.added) - new Date(a.added))[0]?.note || '' : ''

				setFormData({
					customerName: data?.customerName || '',
					customerEmail: data?.customerEmail || '',
					customerTel: data?.customerTel || '',
					source: data?.source || '',
					campaign: data?.campaign || '',
					status: data?.status || '',
					note: latestNote,
					communications: comms,
				})
			} catch (e) {
				console.error('Error fetching lead details:', e)
				setError(e.message || 'Failed to load lead')
			} finally {
				if (alive) setFetching(false)
			}
		})()
		return () => {
			alive = false
		}
	}, [id])

	return (
		<div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
			<div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				{/* Header */}
				<div className="px-6 py-8 border-b border-gray-200">
					<div className="max-w-2xl">
						<h2 className="text-2xl font-bold leading-7 text-gray-900">Lead Information</h2>
						<p className="mt-2 text-sm leading-6 text-gray-600">Update lead details</p>
					</div>
				</div>

				{/* Two-column layout */}
				<div className="grid grid-cols-1 xl:grid-cols-12 xl:gap-8">
					{/* Left: form */}
					<div className="xl:col-span-8">
						<form onSubmit={handleSubmit} className="px-6 py-8">
							{error && (
								<div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
									<div className="text-sm text-red-700">{error}</div>
								</div>
							)}

							<div className="space-y-8">
								<PersonalInfoSection form={formData} setField={setField} />
								<LeadDetailsSection form={formData} setField={setField} />
							</div>

							{/* Actions */}
							<div className="mt-10 pt-6 border-t border-gray-200">
								<div className="flex items-center gap-4">
									<button
										type="submit"
										disabled={fetching || submitting}
										className={`inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 min-w-[120px] ${
											fetching || submitting
												? 'bg-gray-400 text-white cursor-not-allowed'
												: 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-md'
										}`}>
										{submitting ? 'Updating...' : 'Update Lead'}
									</button>

									<button
										type="button"
										onClick={() => navigate('/leads')}
										className="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 min-w-[120px]">
										Cancel
									</button>
								</div>
							</div>
						</form>
					</div>

					{/* Right: communications */}
					<div className="xl:col-span-4 border-l border-gray-200">
						<CommunicationHistory communications={formData.communications} />
					</div>
				</div>
			</div>
		</div>
	)
}
